import { BookOpenText, CalendarPlus, Terminal } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal, flushSync } from "react-dom";
import { useSlashCommands } from "../../hooks/useSlashCommands.ts";
import { SLASH_SCHEDULE_COMMANDS } from "../../lib/trigger-words.ts";
import { cn } from "../../lib/utils.ts";
import type { ContactDropdownPosition } from "./contact-autocomplete.tsx";

export interface SlashCommandDropdownRef {
  handleArrowUp: () => void;
  handleArrowDown: () => void;
  handleEnter: () => boolean;
}

interface SlashCommandDropdownProps {
  query: string;
  position: ContactDropdownPosition;
  onSelect: (commandName: string) => void;
  onSelectScheduling?: (triggerWord: string) => void;
  className?: string;
}

export const SlashCommandDropdown = forwardRef<SlashCommandDropdownRef, SlashCommandDropdownProps>(
  ({ query, position, onSelect, onSelectScheduling, className }, ref) => {
    const { commands } = useSlashCommands();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLSpanElement>(null);
    const [adjustedTop, setAdjustedTop] = useState(position.top);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Filter the 4 base scheduling commands by query (matches name + aliases)
    const filteredScheduling = useMemo(() => {
      if (!onSelectScheduling) return [];
      if (!query) return SLASH_SCHEDULE_COMMANDS;
      const input = query.toLowerCase();
      return SLASH_SCHEDULE_COMMANDS.filter(
        (cmd) =>
          cmd.name.toLowerCase().includes(input) ||
          cmd.aliases.some((a) => a.toLowerCase().includes(input)),
      );
    }, [query, onSelectScheduling]);

    const filteredBuiltins = useMemo(() => {
      const builtins = commands.filter((cmd) => cmd.source === "builtin");
      if (!query) return builtins;
      const input = query.toLowerCase();
      return builtins.filter(
        (cmd) =>
          cmd.name.toLowerCase().includes(input) || cmd.displayName.toLowerCase().includes(input),
      );
    }, [query, commands]);

    const filteredSkills = useMemo(() => {
      const skills = commands.filter((cmd) => cmd.source === "skill");
      if (!query) return skills;
      const input = query.toLowerCase();
      return skills.filter(
        (cmd) =>
          cmd.name.toLowerCase().includes(input) || cmd.displayName.toLowerCase().includes(input),
      );
    }, [query, commands]);

    const totalItems = filteredBuiltins.length + filteredScheduling.length + filteredSkills.length;

    // Reset selected index when filtered results change
    // biome-ignore lint/correctness/useExhaustiveDependencies: need to reset when list changes
    useEffect(() => {
      setSelectedIndex(0);
    }, [totalItems]);

    // Imperatively scroll a given item into view — called directly from
    // arrow-key handlers (via flushSync) so highlight + scroll update in one frame.
    const scrollToItem = useCallback((index: number) => {
      const container = scrollRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-index="${index}"]`) as HTMLElement | null;
      if (!el) return;

      const elTop = el.offsetTop;
      const elBottom = elTop + el.offsetHeight;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;

      if (elBottom > viewBottom) {
        container.scrollTop = elBottom - container.clientHeight;
      } else if (elTop < viewTop) {
        container.scrollTop = elTop;
      }
    }, []);

    // Update the fixed header label based on scroll position.
    // Imperative DOM update avoids re-renders on every scroll event.
    const handleScroll = useCallback(() => {
      const container = scrollRef.current;
      if (!container || !headerRef.current) return;
      const markers = container.querySelectorAll<HTMLElement>("[data-section]");
      let label = "";
      for (const marker of markers) {
        if (marker.offsetTop <= container.scrollTop + 2) {
          label = marker.dataset["section"] ?? label;
        }
      }
      if (label) headerRef.current.textContent = label;
    }, []);

    // Reset header label when filtered sections change.
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset when section visibility changes
    useEffect(() => {
      if (!headerRef.current) return;
      headerRef.current.textContent =
        filteredBuiltins.length > 0
          ? "Commands"
          : filteredScheduling.length > 0
            ? "Schedule"
            : "Skills";
    }, [filteredBuiltins.length, filteredScheduling.length, filteredSkills.length]);

    // Adjust position to anchor bottom (above the textarea)
    // biome-ignore lint/correctness/useExhaustiveDependencies: need to recalculate when list changes size
    useLayoutEffect(() => {
      if (position.anchorBottom && dropdownRef.current) {
        const dropdownHeight = dropdownRef.current.offsetHeight;
        const newTop = position.anchorBottom - dropdownHeight - 8;
        // No room above: open below the box, as the contact menu does.
        setAdjustedTop(newTop < window.scrollY ? position.top : newTop);
      } else {
        setAdjustedTop(position.top);
      }
    }, [position.anchorBottom, position.top, totalItems]);

    useImperativeHandle(
      ref,
      () => ({
        handleArrowUp: () => {
          const next = selectedIndex > 0 ? selectedIndex - 1 : totalItems - 1;
          flushSync(() => setSelectedIndex(next));
          scrollToItem(next);
        },
        handleArrowDown: () => {
          const next = selectedIndex < totalItems - 1 ? selectedIndex + 1 : 0;
          flushSync(() => setSelectedIndex(next));
          scrollToItem(next);
        },
        handleEnter: () => {
          if (totalItems === 0) return false;

          if (selectedIndex < filteredBuiltins.length) {
            const cmd = filteredBuiltins[selectedIndex];
            if (cmd) {
              onSelect(cmd.name);
              return true;
            }
          } else {
            const afterBuiltins = selectedIndex - filteredBuiltins.length;
            if (afterBuiltins < filteredScheduling.length) {
              const cmd = filteredScheduling[afterBuiltins];
              if (cmd) {
                onSelectScheduling?.(cmd.name);
                return true;
              }
            } else {
              const cmd = filteredSkills[afterBuiltins - filteredScheduling.length];
              if (cmd) {
                onSelect(cmd.name);
                return true;
              }
            }
          }
          return false;
        },
      }),
      [
        totalItems,
        selectedIndex,
        filteredScheduling,
        filteredSkills,
        filteredBuiltins,
        onSelect,
        onSelectScheduling,
        scrollToItem,
      ],
    );

    if (totalItems === 0) return null;

    return createPortal(
      <div
        ref={dropdownRef}
        style={{
          position: "absolute",
          top: adjustedTop,
          left: position.left,
          width: 300,
          // Re-enable interaction inside a Radix Dialog, which sets
          // `pointer-events: none` on <body>; this portals there, so without it
          // every row is unclickable. The same override `ContactAutocompleteDropdown` carries.
          pointerEvents: "auto",
        }}
        className={cn(
          "z-9999 flex flex-col min-w-0 max-h-64 overflow-hidden",
          "bg-popover text-popover-foreground rounded-xl shadow-lg border border-border",
          className,
        )}
      >
        {/* Section header — outside scroll, matches @ and scheduling dialogs */}
        <div className="shrink-0 px-3 pt-2 pb-1 flex items-center gap-2">
          <span
            ref={headerRef}
            className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
          >
            {filteredBuiltins.length > 0
              ? "Commands"
              : filteredScheduling.length > 0
                ? "Schedule"
                : "Skills"}
          </span>
          <span className="text-[10px] text-muted-foreground/60">tab to accept</span>
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className={cn(
            "relative min-h-0 overflow-y-auto",
            "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent hover:scrollbar-thumb-border",
          )}
        >
          {/* Commands section */}
          {filteredBuiltins.length > 0 && (
            <>
              <div data-section="Commands" />
              {filteredBuiltins.map((cmd, i) => {
                const isSelected = i === selectedIndex;
                return (
                  <div
                    key={`builtin-${cmd.name}`}
                    data-index={i}
                    className={cn(
                      "flex flex-row items-center gap-2 px-3 py-2 cursor-pointer",
                      isSelected ? "bg-secondary" : "hover:bg-secondary",
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(cmd.name);
                    }}
                    onMouseEnter={() => setSelectedIndex(i)}
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary shrink-0">
                      <Terminal className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <div className="text-sm font-medium truncate">{cmd.displayName}</div>
                      <div className="text-xs text-muted-foreground truncate">/{cmd.name}</div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Schedule section */}
          {filteredScheduling.length > 0 && (
            <>
              <div data-section="Schedule" className="px-3 pt-2 pb-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Schedule
                </span>
              </div>
              {filteredScheduling.map((cmd, i) => {
                const globalIndex = filteredBuiltins.length + i;
                const isSelected = globalIndex === selectedIndex;
                return (
                  <div
                    key={cmd.name}
                    data-index={globalIndex}
                    className={cn(
                      "flex flex-row items-center gap-2 px-3 py-2 cursor-pointer",
                      isSelected ? "bg-secondary" : "hover:bg-secondary",
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelectScheduling?.(cmd.name);
                    }}
                    onMouseEnter={() => setSelectedIndex(globalIndex)}
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary shrink-0">
                      <CalendarPlus className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <div className="text-sm font-medium truncate">{cmd.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {cmd.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Skills section */}
          {filteredSkills.length > 0 && (
            <>
              <div data-section="Skills" className="px-3 pt-2 pb-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Skills
                </span>
              </div>
              {filteredSkills.map((cmd, i) => {
                const globalIndex = filteredBuiltins.length + filteredScheduling.length + i;
                const isSelected = globalIndex === selectedIndex;
                return (
                  <div
                    key={`skill-${cmd.name}`}
                    data-index={globalIndex}
                    className={cn(
                      "flex flex-row items-center gap-2 px-3 py-2 cursor-pointer",
                      isSelected ? "bg-secondary" : "hover:bg-secondary",
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(cmd.name);
                    }}
                    onMouseEnter={() => setSelectedIndex(globalIndex)}
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary shrink-0">
                      <BookOpenText className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <div className="text-sm font-medium truncate">{cmd.displayName}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {cmd.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Fade overlay — sticky to bottom */}
          <div className="sticky bottom-0 h-3 -mt-3 pointer-events-none bg-gradient-to-t from-popover to-transparent" />
        </div>

        {/* Solid spacer so scrollbar clears rounded corners */}
        <div className="shrink-0 h-3 bg-popover" />
      </div>,
      document.body,
    );
  },
);

SlashCommandDropdown.displayName = "SlashCommandDropdown";
